import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import viewQueues from '@/shared/api/fulfilment/queue/viewQueues';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Queue from '@/shared/types/fulfilment/queue.type';

const tableColumnsList = [
  'filled_qty',
  'fulfil_qty',
  'fulfilment_provider_id',
  'fulfilment_provider_key',
  'fulfilment_queue_id',
  'in_account_id',
  'in_order_id',
  'in_tenant_id',
  'inventory_id',
  'post_tax_price',
  'pre_tax_price',
  'product_description',
  'product_id',
  'product_name',
  'product_qty',
  'shipping_profile_id',
  'shipping_provider_key',
  'status',
  'tax_multiplier',
  'ts_created',
  'ts_modified',
  'ts_submit',
  'variant_id',
  'assigned_qty',
  'sku',
];

const tableRequiredColumns = [
  'fulfilment_queue_id',
  'status',
  'sku',
  'product_name',
  'product_qty',
  'assigned_qty',
  'fulfil_qty',
  'filled_qty',
  'ts_modified',
  'actions',
];

type Args = {
  orderId: string;
  getTableStateRows: (value: Queue[]) => void;
};

const useFulfilmentQueueTable_v2 = ({ orderId, getTableStateRows }: Args) => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();
  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const [refreshTableIndex, setRefreshTableIndex] = useState(0);

  const [table, setTable] = useState({
    data: [] as Queue[],
    totalRows: 0,
    isLoading: false,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList,
      tableRequiredColumns: tableRequiredColumns,
    });

  useEffect(() => {
    const fetchQueues = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const response = await viewQueues(
        {
          ...requestBody,
          search: [
            {
              in_order_id: {
                cop: ['='],
                value: orderId,
              },
            },
            requestBody.search.length > 0
              ? requestBody.search.map((item, index) =>
                  index === 0 ? { ...item, op: 'and' } : item,
                )
              : undefined,
          ],
        },
        redirectLogin,
      );

      setTable((prev) => ({ ...prev, isLoading: false }));

      if (response.success) {
        setTable((prev) => ({
          ...prev,
          data: response.data,
          totalRows: response.total_records || 0,
        }));
        getTableStateRows(response.data);
      }
    };

    if (!isEqual(requestBody, requestBodyRef.current) || refreshTableIndex) {
      requestBodyRef.current = requestBody;

      void fetchQueues();
    }
  }, [
    orderId,
    refreshTableIndex,
    requestBody,
    redirectLogin,
    getTableStateRows,
  ]);

  return {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
    setTable,
    setRefreshTableIndex,
  };
};

export default useFulfilmentQueueTable_v2;
